import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { storeLocations, mapCenter, defaultZoom } from './storeData';
import { lockScroll, unlockScroll } from 'utils/scrollLock';
import './StoreLocator.css';
import { IoCloseOutline } from 'react-icons/io5';

// Distancia haversine en km entre dos coordenadas
const haversineKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const StoreLocator = ({ isOpen, onClose, usePortal = true }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showRecentDropdown, setShowRecentDropdown] = useState(false);
  const [viewMode, setViewMode] = useState('map');
  const [selectedStore, setSelectedStore] = useState(null);
  // Controla la clase .is-open del panel de detalle para animar entrada/salida.
  // Se mantiene el panel montado (con selectedStore) durante el cierre para que
  // el slide-out se vea, y luego se limpia el contenido.
  const [detailOpen, setDetailOpen] = useState(false);
  const [stateFilter, setStateFilter] = useState('all');
  const [userLocation, setUserLocation] = useState(null);
  const [isLocating, setIsLocating] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [recentSearches, setRecentSearches] = useState(() => {
    const saved = localStorage.getItem('phys-recent-locations');
    return saved ? JSON.parse(saved) : [];
  });

  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const leafletRef = useRef(null);
  const markersRef = useRef([]);
  const userMarkerRef = useRef(null);
  const inputRef = useRef(null);

  // Lista única de estados para el filtro
  const availableStates = useMemo(() => {
    const set = new Set();
    storeLocations.forEach((s) => {
      if (s.state) set.add(s.state);
    });
    return Array.from(set).sort();
  }, []);

  // Filtrado: aplica búsqueda de texto + filtro por estado, y agrega distancia si hay userLocation
  const filteredStores = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let result = storeLocations.filter((s) => {
      if (stateFilter !== 'all' && s.state !== stateFilter) return false;
      if (!q) return true;
      const haystack = [
        s.name,
        s.business,
        s.address,
        s.city,
        s.state,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
    if (userLocation) {
      result = result
        .map((s) => ({
          ...s,
          distanceKm: haversineKm(userLocation.lat, userLocation.lng, s.lat, s.lng),
        }))
        .sort((a, b) => a.distanceKm - b.distanceKm);
    }
    return result;
  }, [searchQuery, stateFilter, userLocation]);

  // Cerrar con ESC + lock scroll
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      lockScroll();
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      unlockScroll();
    };
  }, [isOpen, onClose]);

  // Inicializar mapa una sola vez al abrir
  useEffect(() => {
    if (!isOpen || !mapContainerRef.current || mapInstanceRef.current) return;

    let cancelled = false;
    const initMap = async () => {
      const L = await import('leaflet');
      await import('leaflet/dist/leaflet.css');
      if (cancelled || !mapContainerRef.current) return;

      const map = L.map(mapContainerRef.current, {
        center: [mapCenter.lat, mapCenter.lng],
        zoom: defaultZoom,
        zoomControl: false,
      });
      L.control.zoom({ position: 'topright' }).addTo(map);
      // Basemap vía MapTiler. La key es un token de cliente (se restringe por
      // dominio en el panel de MapTiler) y se lee de REACT_APP_MAPTILER_KEY.
      // Estilo "positron": look claro/minimalista idéntico al CARTO anterior.
      // Se puede cambiar el estilo con REACT_APP_MAPTILER_STYLE (ej. dataviz,
      // basic-v2, streets-v2).
      const maptilerKey = process.env.REACT_APP_MAPTILER_KEY;
      // Estilo por defecto = mapa "Base Light" personalizado en MapTiler. Se puede
      // sobreescribir con REACT_APP_MAPTILER_STYLE (otro id de mapa o estilo).
      const maptilerStyle =
        process.env.REACT_APP_MAPTILER_STYLE ||
        '10508de2-505a-40dd-a9a1-28ff08371f63';
      if (maptilerKey) {
        L.tileLayer(
          `https://api.maptiler.com/maps/${maptilerStyle}/{z}/{x}/{y}.png?key=${maptilerKey}`,
          {
            attribution:
              '&copy; <a href="https://www.maptiler.com/copyright/" target="_blank" rel="noopener">MapTiler</a> &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>',
            // MapTiler entrega tiles de 512px; sin esto Leaflet (256) los muestra
            // mal alineados/con zoom incorrecto.
            tileSize: 512,
            zoomOffset: -1,
            minZoom: 1,
            maxZoom: 20,
            crossOrigin: true,
          }
        ).addTo(map);
      } else {
        // Sin key configurada: mapa claro "light gray" de Esri (gratis, sin key),
        // look minimalista casi sin color — respaldo para que el mapa se vea bien
        // aunque falle/falte la key de MapTiler.
        console.warn(
          '[StoreLocator] Falta/inválida REACT_APP_MAPTILER_KEY; usando Esri Light Gray de respaldo.'
        );
        L.tileLayer(
          'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
          {
            attribution: 'Tiles &copy; Esri',
            maxZoom: 16,
          }
        ).addTo(map);
      }

      leafletRef.current = L;
      mapInstanceRef.current = map;
      setMapReady(true);
    };

    const id = setTimeout(initMap, 100);
    return () => {
      cancelled = true;
      clearTimeout(id);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        leafletRef.current = null;
        markersRef.current = [];
        userMarkerRef.current = null;
      }
      setMapReady(false);
    };
  }, [isOpen]);

  // Re-pintar marcadores cuando cambia el set filtrado o el mapa termina de inicializarse
  useEffect(() => {
    if (!mapReady) return;
    const L = leafletRef.current;
    const map = mapInstanceRef.current;
    if (!L || !map) return;

    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    filteredStores.forEach((store, index) => {
      const icon = L.divIcon({
        className: 'phys-store-marker',
        html: `<div class="phys-store-marker-inner">${index + 1}</div>`,
        iconSize: [28, 28],
        iconAnchor: [14, 14],
      });
      const marker = L.marker([store.lat, store.lng], { icon }).addTo(map);
      marker.on('click', (e) => {
        L.DomEvent.stopPropagation(e);
        openStore(store);
      });
      markersRef.current.push(marker);
    });

    if (filteredStores.length > 0) {
      const bounds = L.latLngBounds(filteredStores.map((s) => [s.lat, s.lng]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
    }
  }, [filteredStores, mapReady]);

  // Mostrar/ocultar marker del usuario
  useEffect(() => {
    const L = leafletRef.current;
    const map = mapInstanceRef.current;
    if (!L || !map) return;
    if (userMarkerRef.current) {
      userMarkerRef.current.remove();
      userMarkerRef.current = null;
    }
    if (userLocation) {
      const icon = L.divIcon({
        className: 'phys-user-marker',
        html: '<div class="phys-user-marker-inner"></div>',
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      });
      userMarkerRef.current = L.marker([userLocation.lat, userLocation.lng], { icon }).addTo(map);
    }
  }, [userLocation]);

  // Forzar resize de Leaflet al volver de lista a mapa (el contenedor estaba display:none)
  useEffect(() => {
    if (viewMode === 'map' && mapInstanceRef.current) {
      const id = setTimeout(() => mapInstanceRef.current.invalidateSize(), 50);
      return () => clearTimeout(id);
    }
  }, [viewMode]);

  // Buscar tiendas cercanas (geolocalización)
  const handleNearbySearch = () => {
    if (!navigator.geolocation) {
      alert('Tu navegador no soporta geolocalización.');
      return;
    }
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        setStateFilter('all');
        setSearchQuery('');
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setView([latitude, longitude], 11);
        }
        setViewMode('list');
        setIsLocating(false);
      },
      (error) => {
        console.error('Error de geolocalización:', error);
        setIsLocating(false);
        alert('No se pudo obtener tu ubicación. Por favor, busca manualmente.');
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    );
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    const newRecent = [searchQuery, ...recentSearches.filter((s) => s !== searchQuery)].slice(0, 5);
    setRecentSearches(newRecent);
    localStorage.setItem('phys-recent-locations', JSON.stringify(newRecent));
    setShowRecentDropdown(false);
  };

  const handleSelectRecent = (search) => {
    setSearchQuery(search);
    setShowRecentDropdown(false);
  };

  const handleManageAddresses = () => {
    setRecentSearches([]);
    localStorage.removeItem('phys-recent-locations');
    setShowRecentDropdown(false);
  };

  // Abre el panel de detalle (anima entrada vía clase .is-open).
  const openStore = (store) => {
    setSelectedStore(store);
    setDetailOpen(true);
  };

  // Cierra con animación: quita .is-open y limpia el contenido al terminar la
  // transición (320ms, debe coincidir con el CSS).
  const closeDetail = () => {
    setDetailOpen(false);
    window.setTimeout(() => setSelectedStore(null), 320);
  };

  const handleStoreClick = (store) => {
    openStore(store);
    setViewMode('map');
    if (mapInstanceRef.current) {
      mapInstanceRef.current.flyTo([store.lat, store.lng], 15, { duration: 0.6 });
    }
  };

  // Centrar en mapa (reemplaza "Cómo llegar a Google")
  const handleCenterOnMap = (store) => {
    if (typeof store?.lat !== 'number' || typeof store?.lng !== 'number') return;
    setViewMode('map');
    if (mapInstanceRef.current) {
      mapInstanceRef.current.flyTo([store.lat, store.lng], 16, { duration: 0.6 });
    }
  };

  if (!isOpen) return null;

  const showingNearbyResults = !!userLocation && viewMode === 'list';

  const content = (
    <div className={usePortal ? 'phys-store-locator-overlay' : 'phys-store-locator-inline'}>
      <div className="phys-store-locator">
        {/* Panel izquierdo */}
        <div className="phys-sl-search-panel">
          <div className="phys-sl-logo">
            <span>physiology</span>
            <button type="button" className="phys-sl-x" onClick={onClose} aria-label="Cerrar mapa">
              <IoCloseOutline size={28} />
            </button>
          </div>

          <button
            className="phys-sl-nearby-btn"
            onClick={handleNearbySearch}
            disabled={isLocating}
          >
            {isLocating ? <span className="phys-sl-loading"></span> : 'Ver tiendas cercanas'}
          </button>

          <form className="phys-sl-search-form" onSubmit={handleSearch}>
            <span className="phys-sl-search-label">O buscar por</span>
            <div className="phys-sl-input-wrapper">
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setShowRecentDropdown(true)}
                onBlur={() => setTimeout(() => setShowRecentDropdown(false), 150)}
                className="phys-sl-input"
                placeholder=" "
              />
              <label className="phys-sl-input-label">Ciudad o código postal</label>
              <button type="submit" className="phys-sl-submit" aria-label="Buscar">
                <svg width="12" height="12" viewBox="0 0 50 50" fill="currentColor">
                  <path d="M30.1,5.3L50,25.1L30.1,45h-6.6l18-17.6H0v-4.8h41.5l-18-17.6h6.6V5.3z" />
                </svg>
              </button>

              {showRecentDropdown && recentSearches.length > 0 && (
                <div className="phys-sl-dropdown">
                  {recentSearches.map((search, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="phys-sl-dropdown-item"
                      onMouseDown={() => handleSelectRecent(search)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 6v6l4 2" />
                      </svg>
                      {search}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="phys-sl-dropdown-item phys-sl-dropdown-manage"
                    onMouseDown={handleManageAddresses}
                  >
                    Limpiar búsquedas recientes
                  </button>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Panel derecho */}
        <div className="phys-sl-results-panel">
          <div className="phys-sl-results-header">
            <button className="phys-sl-close" onClick={onClose}>
              Seguir comprando
            </button>

            <div className="phys-sl-results-toggle">
              <span className="phys-sl-results-count">
                {filteredStores.length} {filteredStores.length === 1 ? 'Ubicación' : 'Ubicaciones'}
              </span>
              <div className="phys-sl-view-toggle">
                <button
                  className={viewMode === 'map' ? 'active' : ''}
                  onClick={() => setViewMode('map')}
                >
                  Mapa
                </button>
                <button
                  className={viewMode === 'list' ? 'active' : ''}
                  onClick={() => setViewMode('list')}
                >
                  Lista
                </button>
              </div>
            </div>
          </div>

          <div className="phys-sl-content">
            {/* Mapa siempre montado para preservar marcadores; ocultamos con display al ir a lista */}
            <div
              ref={mapContainerRef}
              className="phys-sl-map"
              style={{ display: viewMode === 'map' ? 'block' : 'none' }}
            />

            {viewMode === 'map' && (
              <div
                className={`phys-sl-store-detail ${detailOpen ? 'is-open' : ''}`}
                aria-hidden={!detailOpen}
              >
                {selectedStore && (
                <>
                <button
                  className="phys-sl-detail-close"
                  onClick={closeDetail}
                  aria-label="Cerrar detalle"
                >
                  ×
                </button>

                <div className="phys-sl-detail-info">
                  <h3 className="phys-sl-detail-title">
                    {selectedStore.business || selectedStore.name}
                  </h3>
                  {selectedStore.business && selectedStore.name && (
                    <p className="phys-sl-detail-subtitle">{selectedStore.name}</p>
                  )}

                  {selectedStore.phone && (
                    <a className="phys-sl-detail-row" href={`tel:${selectedStore.phone.replace(/\s/g, '')}`}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                      {selectedStore.phone}
                    </a>
                  )}
                  {selectedStore.address && (
                    <div className="phys-sl-detail-row">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      {selectedStore.address}
                    </div>
                  )}
                  {selectedStore.email && (
                    <a className="phys-sl-detail-row" href={`mailto:${selectedStore.email}`}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                      {selectedStore.email}
                    </a>
                  )}
                </div>

                <button className="phys-sl-directions" onClick={() => handleCenterOnMap(selectedStore)}>
                  Centrar en mapa
                </button>
                </>
                )}
              </div>
            )}

            {viewMode === 'list' && (
              <div className="phys-sl-list">
                <div className="phys-sl-list-header">
                  <strong>
                    {showingNearbyResults
                      ? 'Cercanos a ti'
                      : `Distribuidores (${filteredStores.length})`}
                  </strong>

                  {availableStates.length > 1 && (
                    <select
                      className="phys-sl-state-filter"
                      value={stateFilter}
                      onChange={(e) => setStateFilter(e.target.value)}
                    >
                      <option value="all">Todos los estados</option>
                      {availableStates.map((st) => (
                        <option key={st} value={st}>
                          {st}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {filteredStores.length === 0 ? (
                  <div className="phys-sl-empty">No encontramos resultados.</div>
                ) : (
                  <ul className="phys-sl-stores">
                    {filteredStores.map((store, idx) => (
                      <li key={store.id} className="phys-sl-store-item">
                        <button onClick={() => handleStoreClick(store)}>
                          <span className="phys-sl-store-number">{idx + 1}</span>
                          <div className="phys-sl-store-info">
                            <strong>{store.business || store.name}</strong>
                            {store.address && <span>{store.address}</span>}
                            {store.phone && <span>{store.phone}</span>}
                            {typeof store.distanceKm === 'number' && (
                              <span className="phys-sl-distance">
                                {store.distanceKm < 1
                                  ? `${Math.round(store.distanceKm * 1000)} m`
                                  : `${store.distanceKm.toFixed(1)} km`}
                              </span>
                            )}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return usePortal ? createPortal(content, document.body) : content;
};

export default StoreLocator;
